import {
  ActionConfirmationModal,
  AppModal,
  Button,
  CheckboxFormField,
  Form,
  FormSubmitButton,
  SelectFormField,
  SimpleSelectFormField,
  TextAreaFormField,
  TextFormField,
  Text,
} from "@/components/ui";
import { MultiCategorySelectFormField } from "@/components/ui/FormFields/MultiCategorySelectFormField/MultiCategorySelectFormField";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import {
  useCreateMatterTaskPlanTask,
  useDeleteMatterTaskPlanTask,
  useUpdateMatterTaskPlanTask,
} from "@/lib/data/react-query/matters/matter-task-plan";
import { MatterTaskPlanTaskSchema } from "@/lib/data/schemas/matters/MatterSchema";
import { MATTER_TASK_PLAN_TASK_TRIGGER_TYPE_OPTIONS } from "@/lib/data/static/matters/taskPlanTaskStatics";
import type { MatterSettingStaticOption } from "@/lib/data/requests/matters/matter-setting-statics";
import { MATTER_TASK_PRIORITY_OPTIONS } from "@/lib/data/static/matters/taskStatics";
import { buildMatterStatusGroups } from "@/lib/data/static/matterStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import {
  type MatterRole,
  type MatterStatus,
  type MatterTaskPlanTask,
} from "@/lib/types";
import { TaskAssignmentFields } from "./TaskAssignmentFields";
const MATTER_TASK_PLAN_TASK_FORM_ID = "matter-task-plan-task-editor-form";

const matterTaskPlanTaskModalFieldsetClassName =
  "relative grid grid-cols-1 gap-4 sm:grid-cols-2";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
interface AddOrEditMatterTaskPlanTaskModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  id: string;
  tasks: MatterTaskPlanTask[];
  roles: MatterRole[];
  statuses: MatterStatus[];
  actionOptions: MatterSettingStaticOption[];
  areActionOptionsLoading: boolean;
  isActionOptionsError: boolean;
  editing: boolean;
  activeTask?: MatterTaskPlanTask;
  isReadOnly?: boolean;
}

const formSchema = MatterTaskPlanTaskSchema.pick({
  title: true,
  description: true,
  priority: true,
  triggerStatusId: true,
  triggerSubitem: true,
  triggerTaskId: true,
  assigneeRoleId: true,
  assigneeUserIds: true,
  assigneeBackupUserId: true,
  assignerRoleId: true,
  assignerUserIds: true,
  assignerBackupUserId: true,
  dueDate: true,
  startDate: true,
  repeatable: true,
})
  .extend({
    matterTaskPlanTaskTriggerType: z.enum(
      MATTER_TASK_PLAN_TASK_TRIGGER_TYPE_OPTIONS,
    ),
  })
  .superRefine((data, ctx) => {
    if (data.matterTaskPlanTaskTriggerType === "matterStatus") {
      if (!data.triggerStatusId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Matter status is required",
          path: ["triggerStatusId"],
        });
      }
    }
    if (data.matterTaskPlanTaskTriggerType === "actionTaken") {
      if (!data.triggerSubitem) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Action taken is required",
          path: ["triggerSubitem"],
        });
      }
    }
    if (data.matterTaskPlanTaskTriggerType === "taskCompletion") {
      if (!data.triggerTaskId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Trigger task is required",
          path: ["triggerTaskId"],
        });
      }
    }
    if (data.dueDate !== undefined) {
      const parsedValue = Number(data.dueDate);
      if (
        Number.isNaN(parsedValue) ||
        parsedValue < 0 ||
        !Number.isInteger(parsedValue)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Due date must be a whole number of days",
          path: ["dueDate"],
        });
      }
    }
    if (data.startDate !== undefined) {
      const parsedValue = Number(data.startDate);
      if (
        Number.isNaN(parsedValue) ||
        parsedValue < 0 ||
        !Number.isInteger(parsedValue)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Start date must be a whole number of days",
          path: ["startDate"],
        });
      }
    }
    if (
      data.startDate !== undefined &&
      data.dueDate !== undefined &&
      data.startDate !== null &&
      data.dueDate !== null
    ) {
      const startDate = data.startDate;
      const dueDate = data.dueDate;

      if (startDate >= dueDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Start date delay must be less than due date delay",
          path: ["startDate"],
        });
      }
    }
  });

type FormValues = z.infer<typeof formSchema>;
type AssignmentSource = "role" | "user";

export const AddOrEditMatterTaskPlanTaskModal = ({
  open,
  setOpen,
  id,
  tasks,
  editing,
  activeTask,
  isReadOnly,
  roles,
  statuses,
  actionOptions,
  areActionOptionsLoading,
  isActionOptionsError,
}: AddOrEditMatterTaskPlanTaskModalProps) => {
  const { hasActionPermission } = useUser();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isMatterStatusOpen, setIsMatterStatusOpen] = useState(false);
  const [assigneeSource, setAssigneeSource] =
    useState<AssignmentSource>("role");
  const [assignerSource, setAssignerSource] =
    useState<AssignmentSource>("role");
  const matterStatusGroups = useMemo(
    () => buildMatterStatusGroups(statuses),
    [statuses],
  );

  const defaultFormValues = useMemo(
    () => ({
      title: "",
      description: "",
      priority: undefined,
      matterTaskPlanTaskTriggerType: "taskCompletion" as const,
      triggerSubitem: undefined,
      triggerTaskId: undefined,
      triggerStatusId: undefined,
      assigneeRoleId: "",
      assigneeUserIds: [],
      assigneeBackupUserId: "",
      assignerRoleId: "",
      assignerUserIds: [],
      assignerBackupUserId: "",
      dueDate: null,
      startDate: null,
      repeatable: false,
    }),
    [],
  );

  const inferredTaskTriggerType = useMemo(() => {
    if (activeTask?.triggerStatusId) {
      return "matterStatus";
    }
    if (activeTask?.triggerSubitem) {
      return "actionTaken";
    }
    if (activeTask?.triggerTaskId) {
      return "taskCompletion";
    }
    return "taskCompletion";
  }, [activeTask]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    values: editing
      ? {
          ...defaultFormValues,
          ...activeTask,
          assigneeRoleId: activeTask?.assigneeRoleId ?? "",
          assigneeUserIds:
            (activeTask?.assigneeUserIds ?? []).length > 0
              ? (activeTask?.assigneeUserIds ?? [])
              : activeTask?.assigneeUserId
                ? [activeTask.assigneeUserId]
                : [],
          assigneeBackupUserId: activeTask?.assigneeBackupUserId ?? "",
          assignerRoleId: activeTask?.assignerRoleId ?? "",
          assignerUserIds:
            (activeTask?.assignerUserIds ?? []).length > 0
              ? (activeTask?.assignerUserIds ?? [])
              : activeTask?.assignerUserId
                ? [activeTask.assignerUserId]
                : [],
          assignerBackupUserId: activeTask?.assignerBackupUserId ?? "",
          matterTaskPlanTaskTriggerType: inferredTaskTriggerType,
          triggerStatusId: activeTask?.triggerStatusId ?? undefined,
          triggerSubitem: activeTask?.triggerSubitem ?? undefined,
          triggerTaskId: activeTask?.triggerTaskId ?? undefined,
          repeatable: activeTask?.repeatable ?? false,
        }
      : defaultFormValues,
    mode: "onSubmit",
  });

  const updateTask = useUpdateMatterTaskPlanTask();
  const createTask = useCreateMatterTaskPlanTask();
  const deleteTask = useDeleteMatterTaskPlanTask();
  const isFormLoading =
    updateTask.isPending || createTask.isPending || deleteTask.isPending;

  useEffect(() => {
    setAssigneeSource(
      (activeTask?.assigneeUserIds ?? []).length || activeTask?.assigneeUserId
        ? "user"
        : "role",
    );
    setAssignerSource(
      (activeTask?.assignerUserIds ?? []).length || activeTask?.assignerUserId
        ? "user"
        : "role",
    );
  }, [activeTask, open]);

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    const {
      matterTaskPlanTaskTriggerType,
      triggerStatusId,
      triggerSubitem,
      triggerTaskId,
      ...taskData
    } = data;
    const normalizedTaskData = {
      ...taskData,
      assigneeRoleId:
        assigneeSource === "role" ? taskData.assigneeRoleId || null : null,
      assigneeUserIds:
        assigneeSource === "user" ? taskData.assigneeUserIds : [],
      assigneeBackupUserId:
        assigneeSource === "role" &&
        taskData.assigneeRoleId &&
        taskData.assigneeBackupUserId
          ? taskData.assigneeBackupUserId
          : null,
      assignerRoleId:
        assignerSource === "role" ? taskData.assignerRoleId || null : null,
      assignerUserIds:
        assignerSource === "user" ? taskData.assignerUserIds : [],
      assignerBackupUserId:
        assignerSource === "role" &&
        taskData.assignerRoleId &&
        taskData.assignerBackupUserId
          ? taskData.assignerBackupUserId
          : null,
    };
    if (editing) {
      if (updateTask.isPending) {
        return;
      }
      await updateTask.mutateAsync({
        id: activeTask?.id ?? "",
        taskPlanId: id,
        dateModified: activeTask?.dateModified ?? "",
        ...normalizedTaskData,
        ...(matterTaskPlanTaskTriggerType === "matterStatus"
          ? { triggerStatusId }
          : matterTaskPlanTaskTriggerType === "actionTaken"
            ? { triggerSubitem }
            : { triggerTaskId }),
      });
    } else {
      if (createTask.isPending) {
        return;
      }
      await createTask.mutateAsync({
        taskPlanId: id,
        ...normalizedTaskData,
        ...(matterTaskPlanTaskTriggerType === "matterStatus"
          ? { triggerStatusId }
          : matterTaskPlanTaskTriggerType === "actionTaken"
            ? { triggerSubitem }
            : { triggerTaskId }),
      });
    }
    setOpen(false);
  };

  const handleDelete = async () => {
    if (!activeTask) {
      return;
    }
    setIsDeleting(false);
    setOpen(false);
    await deleteTask.mutateAsync({
      id: activeTask.id,
      taskPlanId: id,
    });
  };

  useEffect(() => {
    if (!open) {
      form.reset(defaultFormValues);
    }
  }, [open, form, defaultFormValues]);

  const modalTitle = editing ? "Edit task" : "Add task";
  const modalDescription = editing
    ? "Update the default task details below."
    : "Add a task to this plan.";

  const showDeleteInFooter =
    !isReadOnly &&
    editing &&
    hasActionPermission(
      ACTION_PERMISSION_KEYS.matterTaskPlanTask,
      ACTION_PERMISSION_VALUES.delete,
    );

  const deleteButton = showDeleteInFooter && (
    <div>
      <Button
        type="button"
        variant="destructive"
        onClick={() => {
          setIsDeleting(true);
        }}
      >
        Delete
      </Button>
    </div>
  );

  const cancelAndSubmitFooter = (
    <div className="flex grow justify-end space-x-2">
      <FormCancelButton
        label={isReadOnly ? "Close" : "Cancel"}
        setOpen={setOpen}
        isDirty={form.formState.isDirty}
      />
      {!isReadOnly && (
        <FormSubmitButton
          submitFormId={MATTER_TASK_PLAN_TASK_FORM_ID}
          loading={updateTask.isPending || createTask.isPending}
          disabled={isFormLoading}
        />
      )}
    </div>
  );

  return (
    <>
      <Form {...form}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={modalTitle}
          description={modalDescription}
          size="2xl"
          loading={isFormLoading}
          isDirty={form.formState.isDirty}
          overlayClassName="z-[108]"
          className="z-[109]"
          footer={
            <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
              {deleteButton}
              {cancelAndSubmitFooter}
            </div>
          }
        >
          <form
            id={MATTER_TASK_PLAN_TASK_FORM_ID}
            className="relative"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <fieldset
              disabled={isReadOnly}
              className={matterTaskPlanTaskModalFieldsetClassName}
            >
              <div className="col-span-full">
                <Text variant="subHeading" className="my-4 border-b pb-2">
                  Trigger information
                </Text>
              </div>
              <SelectFormField
                label="Trigger type"
                itemsKey="value"
                formatItemLabel={(item) => item?.label}
                register={form.register("matterTaskPlanTaskTriggerType")}
                onChange={() => {
                  // When we change the trigger type, we need to clear the fields specific to the trigger type
                  form.setValue("triggerStatusId", "");
                  form.setValue("triggerSubitem", "");
                  form.setValue("triggerTaskId", "");
                }}
                placeholder="Select trigger type"
                items={[
                  { label: "Task completion", value: "taskCompletion" },
                  { label: "Matter status", value: "matterStatus" },
                  { label: "Action taken", value: "actionTaken" },
                ]}
              />
              {form.watch("matterTaskPlanTaskTriggerType") ===
                "matterStatus" && (
                <MultiCategorySelectFormField
                  label="Matter status*"
                  items={matterStatusGroups}
                  placeholder="Select matter status"
                  register={form.register("triggerStatusId")}
                  open={isMatterStatusOpen}
                  setOpen={setIsMatterStatusOpen}
                />
              )}
              {form.watch("matterTaskPlanTaskTriggerType") ===
                "actionTaken" && (
                <SelectFormField
                  label="Action taken*"
                  items={actionOptions}
                  isItemsLoading={areActionOptionsLoading}
                  isItemsError={isActionOptionsError}
                  itemsKey="value"
                  formatItemLabel={(item) => item?.label}
                  placeholder="Select action taken"
                  register={form.register("triggerSubitem")}
                />
              )}
              {form.watch("matterTaskPlanTaskTriggerType") ===
                "taskCompletion" && (
                <SelectFormField
                  label="Trigger task*"
                  items={tasks.map((task) => ({
                    label: task.title,
                    value: task.id,
                    context: task.description,
                  }))}
                  itemsKey="value"
                  formatItemLabel={(item) => item?.label}
                  formatItemContext={(item) => item?.context ?? undefined}
                  placeholder="Select task completion"
                  register={form.register("triggerTaskId")}
                  optionsClassName="line-clamp-2 max-w-[500px]"
                />
              )}
              <div className="col-span-full">
                <Text variant="subHeading" className="my-4 border-b pb-2">
                  Task information
                </Text>
              </div>
              <TextFormField label="Title*" register={form.register("title")} />
              <div className="col-span-2">
                <TextAreaFormField
                  textAreaClassName="field-sizing-content max-h-[10lh]"
                  label="Description"
                  register={form.register("description")}
                />
              </div>
              <TaskAssignmentFields
                label="Assigner"
                roles={roles}
                source={assignerSource}
                enabled={open}
                selectedUserIds={form.watch("assignerUserIds")}
                backupUserId={form.watch("assignerBackupUserId")}
                roleRegister={form.register("assignerRoleId")}
                usersRegister={form.register("assignerUserIds")}
                backupRegister={form.register("assignerBackupUserId")}
                onSourceChange={(source) => {
                  setAssignerSource(source);
                  if (source === "role") form.setValue("assignerUserIds", []);
                  else {
                    form.setValue("assignerRoleId", "");
                    form.setValue("assignerBackupUserId", "");
                  }
                }}
              />
              <TaskAssignmentFields
                label="Assignee"
                roles={roles}
                source={assigneeSource}
                enabled={open}
                selectedUserIds={form.watch("assigneeUserIds")}
                backupUserId={form.watch("assigneeBackupUserId")}
                roleRegister={form.register("assigneeRoleId")}
                usersRegister={form.register("assigneeUserIds")}
                backupRegister={form.register("assigneeBackupUserId")}
                onSourceChange={(source) => {
                  setAssigneeSource(source);
                  if (source === "role") form.setValue("assigneeUserIds", []);
                  else {
                    form.setValue("assigneeRoleId", "");
                    form.setValue("assigneeBackupUserId", "");
                  }
                }}
              />
              <SimpleSelectFormField
                label="Priority"
                items={MATTER_TASK_PRIORITY_OPTIONS}
                register={form.register("priority")}
                placeholder="Select priority"
              />
              <TextFormField
                label="Start (days after trigger)"
                placeholder="Enter number of days"
                register={form.register("startDate")}
              />
              <TextFormField
                label="Due (days after trigger)"
                placeholder="Enter number of days"
                register={form.register("dueDate")}
              />
              <CheckboxFormField
                label="Repeatable"
                register={form.register("repeatable")}
                className="col-span-1"
              />
            </fieldset>
          </form>
        </AppModal>
      </Form>

      <ActionConfirmationModal
        title="Are you sure you want to delete this task?"
        open={isDeleting}
        setOpen={setIsDeleting}
        afterActionFn={handleDelete}
      />
    </>
  );
};
